import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../helpers/apiUtils';

/**
 * Die reine Ablegefläche: Bild hineinziehen oder klicken, Upload, Vorschau, Fehlermeldung.
 *
 * Herausgelöst aus `ImageUploadField` (2026-09-03), weil das Eigenschaften-Panel des
 * Baustein-Editors genau diesen Teil braucht — aber NICHT dessen URL-Feld: jenes ruft `onChange`
 * pro Tastendruck, was im Panel einen Undo-Schritt je Zeichen bedeutet. `ImageUploadField` ist
 * seither ein Komposit über dieser Komponente und behält seinen Vertrag unverändert
 * (ImageUploadField.test.tsx hält ihn fest).
 *
 * ⚠ Zwei Dinge, die diese Komponente ihren Aufrufern GARANTIERT und die beide mit Absicht hier
 * und nicht im Aufrufer stehen:
 *  - Bei mehreren schnell hintereinander gestarteten Uploads gewinnt der ZULETZT gestartete.
 *    Ohne die Sequenznummer meldet eine langsamere frühere Anfrage ihre URL nach der späteren
 *    und überschreibt sie — sichtbar erst, wenn zwei Dateien schnell nacheinander abgelegt werden.
 *  - Das Dateifeld wird nach jedem Upload zurückgesetzt, sonst löst dieselbe Datei ein zweites Mal
 *    kein `change` aus.
 */

/**
 * Die MIME-Liste, die der Server annimmt — Quelle ist `IMAGE_EXT` in
 * `node-app/src/routes/broker-content.ts`, `webinar-lp.ts` und `newsletter-redlinks.ts`
 * (alle drei führen dieselben fünf Einträge).
 *
 * ⚠ `image/jpg` gehört dazu, obwohl es kein registrierter Typ ist: manche Systeme melden ihn für
 * eine `.jpg`-Datei, und alle drei Routen nehmen ihn. Als die Prüfung unten dazukam, fehlte er
 * hier — ein solcher Drop wurde damit VOR dem Request abgewiesen, obwohl der Server ihn genommen
 * hätte, und die Meldung lautete widersinnig „JPG nimmt der Upload nicht (nur JPG, …)".
 * Betroffen waren nicht nur der Baustein-Editor, sondern über `ImageUploadField` auch die sechs
 * Bestandsaufrufer in vier Dialogen (Gegenlesung 2026-09-03).
 *
 * Eine Konstante für BEIDES — das `accept`-Attribut und die Prüfung in `upload` —, damit die
 * Liste nicht an zwei Stellen auseinanderlaufen kann. Für den Dateidialog ist der nicht
 * registrierte Typ folgenlos.
 */
export const BILD_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
export const BILD_HINWEIS = 'Bild hierher ziehen oder klicken (JPG / PNG / WebP / GIF, max 5 MB)';

interface ImageDropZoneProps {
  /** Vollständiger Upload-Endpunkt inkl. kind-Parameter. */
  uploadUrl: string;
  /** Aktuelle URL — nur für die Vorschau; die Komponente hält keinen eigenen Wert. */
  value: string | null;
  /** Erfolgreicher Upload. Bekommt die ABSOLUTE CDN-URL. */
  onUploaded: (url: string) => void;
  disabled?: boolean;
  /** MIME-Liste fürs Dateifeld. Vorgabe = das, was die Upload-Route annimmt (kein SVG). */
  accept?: string;
  /** Text in der leeren Fläche. */
  hinweis?: string;
  /** Mindesthöhe der Fläche. Vorgabe 72 — der Bestandswert der sechs Dialog-Aufrufer. */
  minHeight?: number;
  /** Grösste Vorschauhöhe. Vorgabe 56 — ebenfalls Bestand. */
  previewMaxHeight?: number;
  /** Grösste Vorschaubreite. Vorgabe 160 — ebenfalls Bestand. */
  previewMaxWidth?: number;
}

const ImageDropZone: React.FC<ImageDropZoneProps> = ({
  uploadUrl,
  value,
  onUploaded,
  disabled = false,
  accept = BILD_ACCEPT,
  hinweis = BILD_HINWEIS,
  minHeight = 72,
  previewMaxHeight = 56,
  previewMaxWidth = 160,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // A saved relative path can't render as a preview from the dashboard origin — fall back to
  // the placeholder instead of showing an empty/broken box.
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => setPreviewFailed(false), [value]);

  // Laufende Nummer je gestartetem Upload; nur die jeweils höchste darf noch schreiben.
  const laufendeNummer = useRef(0);

  /**
   * Dateityp VOR dem Upload prüfen — nicht, um den Server zu ersetzen, sondern um eine
   * brauchbare Meldung zu geben.
   *
   * ⚠ `accept` am Dateifeld gilt nur für den Dateidialog; ein DROP geht daran vorbei. Ohne diese
   * Prüfung landet ein SVG bei multer, dessen `fileFilter` es STILL verwirft — `req.file` ist
   * dann leer und die Route antwortet `No image uploaded (field "image")`. Im Browser gemessen
   * 2026-09-03: der Redakteur liest eine englische Meldung über ein Feld namens „image" und
   * erfährt weder den Grund noch den Ausweg. Der Riegel bleibt serverseitig, hier steht nur der
   * Satz, der jemandem hilft.
   */
  const typErlaubt = (typ: string) =>
    accept
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .includes((typ || '').toLowerCase());

  const upload = async (file: File) => {
    if (!typErlaubt(file.type)) {
      setError(
        `${file.name.split('.').pop()?.toUpperCase() || 'Dieses Format'} nimmt der Upload nicht ` +
          '(nur JPG, PNG, WebP, GIF). Die Adresse lässt sich unten von Hand eintragen.',
      );
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const nummer = (laufendeNummer.current += 1);
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('image', file);
      // api (axios) auto-attaches auth + the multipart boundary; fetchData can't (forces JSON).
      const res = await api.post(uploadUrl, form);
      if (nummer !== laufendeNummer.current) return;
      const body = res.data as { success: boolean; data?: { url: string }; error?: string };
      if (body.success && body.data?.url) onUploaded(body.data.url);
      else setError(body.error || 'Upload fehlgeschlagen');
    } catch (err) {
      if (nummer !== laufendeNummer.current) return;
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error || e.message || 'Upload fehlgeschlagen');
    } finally {
      // Ein überholter Lauf darf den Spinner des aktuellen nicht ausschalten.
      if (nummer === laufendeNummer.current) setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <>
      <Box
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={disabled ? (e) => e.preventDefault() : onDrop}
        onClick={() => {
          if (!disabled) inputRef.current?.click();
        }}
        sx={{
          border: '1px dashed',
          borderColor: dragOver ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          bgcolor: dragOver ? 'action.hover' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight,
        }}
      >
        {uploading ? (
          <CircularProgress size={22} />
        ) : value && !previewFailed ? (
          <img
            src={value}
            alt=""
            style={{ maxHeight: previewMaxHeight, maxWidth: previewMaxWidth, objectFit: 'contain' }}
            onError={() => setPreviewFailed(true)}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {hinweis}
          </Typography>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
        }}
      />
      {error && (
        <Typography variant="caption" color="error" display="block">
          {error}
        </Typography>
      )}
    </>
  );
};

export default ImageDropZone;
